import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, Container, Grid, makeStyles, Typography } from '@material-ui/core';

import ChangePassword from '../../models/PasswordReset';
import User from '../../models/User';
import EditFields from '../common/EditFields';
import UserSignature from '../editWorkspace/UserSignature';

const useStyles = makeStyles(() => {
  return {
    description: {
      color: 'rgba(0, 0, 0, 0.38)',
      fontSize: '0.8rem',
      lineHeight: 1.8,
      marginBottom: '1rem',
    },
    detailsContainer: {
      borderRight: '1px solid #E7E7E7',
      paddingBottom: '60px',
      paddingRight: '60px',
      textAlign: 'left',
    },
    formTitle: {
      color: '#172B4D',
      fontSize: '30px',
      fontWeight: '500',
      marginBottom: '1rem',
    },
    headerContainer: {
      paddingBottom: '30px',
      width: '100%',
    },
    mainContainer: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: '50px',
    },
    passwordContainer: {
      paddingBottom: '40px',
      paddingLeft: '60px',
      textAlign: 'left',
    },
    selectLabels: {
      color: '#172B4D',
      fontSize: '16px',
      fontWeight: '500',
      marginBottom: '5px',
    },
    signatureContainer: {
      marginTop: '4rem',
    },
  };
});

export default ({ userDetails, onUpdate, onChangePassword, validationErrors, onRequestUpdateEmail }) => {
  const [errors, setErrors] = useState(validationErrors);
  const detailsFieldRef = useRef();
  const passwordFieldRef = useRef();
  const classes = useStyles();
  const canEditSignature = ['super-admin', 'admin', 'creator'].includes(userDetails.role);

  useEffect(() => {
    setErrors(validationErrors);
  }, [validationErrors]);

  const detailsFields = useMemo(
    () => [
      { label: 'Name', labelPosition: 'top', name: 'name', type: 'text' },
      {
        label: 'Date of Birth',
        labelPosition: 'top',
        name: 'dateOfBirth',
        placeholder: 'MM/DD/YYYY',
        type: 'date',
      },
      { label: 'Telephone', labelPosition: 'top', name: 'phoneNumber', type: 'phone' },
      { disabled: true, label: 'Email Address', labelPosition: 'top', name: 'email', type: 'text' },
      {
        items: [
          { label: 'Female', value: 'female' },
          { label: 'Male', value: 'male' },
          { label: 'Other', value: 'other' },
        ],
        label: 'Gender',
        labelPosition: 'top',
        name: 'gender',
        placeholder: 'Select',
        type: 'select',
      },
    ],
    []
  );
  const passwordFields = useMemo(
    () => [
      { hidePasswordIcon: true, label: 'Old Password', labelPosition: 'top', name: 'currentPassword', placeholder: '***********', type: 'password' },
      { hidePasswordIcon: true, label: 'New Password', labelPosition: 'top', name: 'newPassword', placeholder: '***********', type: 'password' },
      { hidePasswordIcon: true, label: 'Repeat Password', labelPosition: 'top', name: 'repeatPassword', placeholder: '***********', type: 'password' },
    ],
    []
  );

  const onUpdateClick = async () => {
    const validValues = await detailsFieldRef.current.getValidData();
    if (validValues) {
      onUpdate(validValues);
    }
  };
  const onChangePasswordClick = async () => {
    const validValues = await passwordFieldRef.current.getValidData();

    if (validValues) {
      onChangePassword(validValues);
    }
  };

  const renderHeader = () => (
    <div>
      <Grid container className={classes.headerContainer}>
        <Grid item component="div" xs={5} style={{ paddingRight: '80px' }}>
          <Typography variant="h4" className={`${classes.formTitle} page-header`} align="left">
            My Account
          </Typography>
          <Typography variant="body1" className={`${classes.description} body-copy`} align="left">
            Update your details or change your password. Note that once a password is changed it might take a few minutes to reflect on our system.
          </Typography>
        </Grid>
      </Grid>
    </div>
  );

  return (
    <Container component="main" maxWidth="lg" className={classes.mainContainer}>
      {renderHeader()}
      <Grid container>
        <Grid item container>
          <Grid item xs={4} className={classes.detailsContainer}>
            <Typography variant="h4" className={`${classes.formTitle} secondary-header`} align="left">
              My Details
            </Typography>
            <EditFields
              validationErrors={errors}
              data={userDetails}
              fields={detailsFields}
              useLabel
              ref={detailsFieldRef}
              labelPosition="top"
              labelClass={classes.selectLabels}
              validate={User.validateCreate}
            />
            <Button color="primary" style={{ marginRight: '8px', marginTop: '1rem' }} variant="contained" size="medium" onClick={onUpdateClick}>
              Update My Details
            </Button>

            <Button color="primary" style={{ marginTop: '1rem' }} variant="contained" size="medium" onClick={onRequestUpdateEmail}>
              Update My Email
            </Button>
          </Grid>
          <Grid item xs={8} className={classes.passwordContainer}>
            <Grid item xs={6}>
              <Typography variant="h4" className={`${classes.formTitle} secondary-header`} align="left">
                Change Password
              </Typography>
              <EditFields
                validationErrors={errors}
                fields={passwordFields}
                useLabel
                ref={passwordFieldRef}
                labelPosition="top"
                hidePasswordIcon
                labelClass={classes.selectLabels}
                validate={ChangePassword.validateCreate}
              />
              <Button color="primary" style={{ marginTop: '1rem' }} variant="contained" size="medium" onClick={onChangePasswordClick}>
                Change Password
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Box className={classes.signatureContainer}>{canEditSignature && <UserSignature user={userDetails} />}</Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};
